import type { LazyQueryHookOptions, TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { useCallback } from 'react';
import type { Asset, Query, QueryAssetV2Args } from '@/types/GraphqlSchema';

const ASSET_DETAILS_FIELDS = `
  id
  legacyId
  basin {
    name
  }
  breakevenOil
  country {
    id
    displayName
    name
    isoCode
    haveLiquidsReserves
  }
  currentDate
  developmentStatus {
    name
  }
  developmentType {
    name
  }
  discoveryDate
  displayName
  estimatedCurrentDate
  expectedProjectSanction
  firstProductionDate
  fiscalRegime
  formattedCurYrTotalProdIntensityKgco2ePerBoe
  formattedEstimatedRecentProdGasMmcfd
  formattedEstimatedRecentProdLiqBbld
  formattedGasEurBcf
  formattedGasIpMmcfd
  formattedLiquidsEurMmbbl
  formattedLiquidsIpBbld
  formattedNpv
  formattedRecentProdGasMmcfd
  formattedRecentProdLiquidsBbld
  formattedRemainingCapex
  formattedRemainingReserves
  formattedWeaProdGasMmcfd
  formattedWeaProdLiquidsBbld
  geom
  hydrocarbonType
  isHpht
  licenseExpirationDate
  mAndAOpportunity
  name
  participations {
    effectiveDate
    formattedPercentStake
    percentStake
    isCompanyOperator
    company {
      name
    }
    mAndAOpportunity
  }
  peakProductionDate
  reportUpdatedDate
  reserve {
    formattedTotalRemainingReservesLiquidsMmbbl
    formattedTotalRemainingReservesOilMmbbl
    formattedTotalRemainingReservesGasBcf
  }
  shoreStatus {
    name
  }
  similarAssets
`;

type AssetDetailsQueryData = Pick<Query, 'assetV2'>;

const ASSET_DETAILS_QUERY: TypedDocumentNode<AssetDetailsQueryData, QueryAssetV2Args> = gql`
  query assetDetailsV2($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      ${ASSET_DETAILS_FIELDS}
    }
  }
`;

type AssetDetailsQueryOptions = LazyQueryHookOptions<AssetDetailsQueryData, QueryAssetV2Args>;

export const useAsset = (options?: AssetDetailsQueryOptions) => {
  const queryOptions: AssetDetailsQueryOptions = {
    errorPolicy: 'all',
    ...(options ?? {}),
  };

  const [getAssetDetails, { data, loading, error }] = useLazyQuery(
    ASSET_DETAILS_QUERY,
    queryOptions,
  );

  const fetchAssetDetails = useCallback(
    (assetId: Asset['id']) => {
      getAssetDetails({
        variables: {
          input: { id: assetId },
        },
      });
    },
    [getAssetDetails],
  );

  return {
    fetchAssetDetails,
    data,
    loading,
    error,
    asset: data?.assetV2 ?? null,
  };
};
